import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Share2, Link2, CheckCircle2, Trash2, ExternalLink, Copy } from 'lucide-react';
import { toast } from 'sonner';

interface DataRoomSharingProps {
  companyId: string;
}

interface ShareLink {
  id: string;
  file_id: string;
  email: string;
  token: string;
  created_at: string;
  expires_at: string;
  nda_signed: boolean;
  revoked: boolean;
  signed_url: string;
  data_room_files?: {
    file_name: string;
  };
}

export default function DataRoomSharing({ companyId }: DataRoomSharingProps) {
  const [links, setLinks] = useState<ShareLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [email, setEmail] = useState('');
  const [fileId, setFileId] = useState('');
  const [files, setFiles] = useState<any[]>([]);

  async function load() {
    setLoading(true);
    const [{ data: l }, { data: f }] = await Promise.all([
      supabase
        .from('data_room_links')
        .select('*, data_room_files(file_name)')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false }),
      supabase.from('data_room_files').select('id, file_name').eq('company_id', companyId),
    ]);
    setLinks(l || []);
    setFiles(f || []);
    setLoading(false);
  }

  async function createLink() {
    if (!fileId || !email) {
      toast.error('Please select a file and enter an email');
      return;
    }

    setSharing(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-dataroom-link', {
        body: { companyId, fileId, email },
      });

      if (error) throw error;

      // Copy link to clipboard
      const accessUrl = `${window.location.origin}/dataroom-access?token=${data.link.token}`;
      await navigator.clipboard.writeText(accessUrl);

      toast.success('Secure link created and copied to clipboard. Share it with ' + email);
      setEmail('');
      setFileId('');
      load();
    } catch (error: any) {
      console.error('Error creating link:', error);
      toast.error(error.message || 'Failed to create link');
    } finally {
      setSharing(false);
    }
  }

  function copyLinkToClipboard(token: string) {
    const accessUrl = `${window.location.origin}/dataroom-access?token=${token}`;
    navigator.clipboard.writeText(accessUrl);
    toast.success('Access link copied to clipboard');
  }

  async function revokeLink(id: string) {
    try {
      const { error } = await supabase.functions.invoke('revoke-dataroom-link', {
        body: { linkId: id },
      });

      if (error) throw error;

      toast.success('Link revoked successfully');
      load();
    } catch (error: any) {
      console.error('Error revoking link:', error);
      toast.error(error.message || 'Failed to revoke link');
    }
  }

  useEffect(() => {
    load();
  }, [companyId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Share2 className="h-5 w-5" />
          Data Room Sharing & Access
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-2">
          <Select value={fileId} onValueChange={setFileId}>
            <SelectTrigger>
              <SelectValue placeholder="Select file to share" />
            </SelectTrigger>
            <SelectContent>
              {files.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.file_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            type="email"
            placeholder="Recipient email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <Button onClick={createLink} disabled={sharing || !fileId || !email}>
            {sharing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Share2 className="h-4 w-4 mr-1" />
                Share
              </>
            )}
          </Button>
        </div>

        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        ) : links.length ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {links.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="font-medium">
                      {l.data_room_files?.file_name || 'Unknown'}
                    </TableCell>
                    <TableCell>{l.email}</TableCell>
                    <TableCell>{new Date(l.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(l.expires_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {l.revoked ? (
                          <span className="text-destructive">Revoked</span>
                        ) : l.nda_signed ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            <span className="text-green-600">Active</span>
                          </>
                        ) : (
                          <span className="text-muted-foreground">Pending NDA</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2 justify-end">
                        {!l.revoked && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyLinkToClipboard(l.token)}
                            >
                              <Copy className="h-4 w-4 mr-1" />
                              Copy Link
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(l.signed_url, '_blank')}
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              Open
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => revokeLink(l.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Revoke
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">
            No shared links yet. Create a secure link to share files.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
